import { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { customersApi, usersApi } from '@/lib/api';
import { Building2, Plus, Search, Trash2 } from 'lucide-react';
import {
  Button,
  Card,
  Combobox,
  ConfirmDialog,
  DataTable,
  EmptyState,
  IconButton,
  Input,
  Pagination,
  Spinner,
  Status,
  formatDatum,
  statusInfo,
  type Column,
  type DataTableSort,
} from '@elbklar/ui';
import { StatusDropdown } from '@/features/customers/components/StatusDropdown';
import { CrmPageHeader } from '@/components/layout/CrmPageHeader';
import { SuchTreffer, suchZiel, useGlobaleSuche } from '@/components/layout/Suche';
import { usePersistedFilters } from '@/hooks/usePersistedFilters';
import type { Customer, CustomerStatus } from '@/types';
import { KUNDE_STATUSES } from '@/types';

const PER_PAGE = 50;

// Kunden-Seite zeigt nur Kunde-Phase-Status (Leads leben unter /leads).
// Text und Ton kommen aus STATUS des Design Systems.
const KUNDE_STATUS_ENTRIES: Array<[CustomerStatus, string]> = KUNDE_STATUSES.map(
  (s) => [s, statusInfo(s).label],
);

const defaultFilters = {
  search: '',
  status: '' as CustomerStatus | '',
  city: '',
  assigned_to: '',
  sort_by: 'company_name',
  sort_order: 'asc' as 'asc' | 'desc',
};

export function CustomersPage() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [filters, updateFilters, resetFilters] = usePersistedFilters('customers', defaultFilters);
  const [page, setPage] = useState(1);
  // Am Handy ist das Suchfeld oben die globale Suche (Kunden, Partner,
  // Kontakte), am Rechner filtert es die Liste; die globale Suche steht dort im
  // Seitenkopf. Beide nutzen dieselbe Suche (components/layout/Suche).
  const [mobilSuche, setMobilSuche] = useState('');
  const suche = useGlobaleSuche(mobilSuche);

  // Bestaetigung als In-App-Dialog statt window.confirm (Trockenlauf 12.10.2026, Befund 9).
  // Der Dialog wartet auf das Loeschen und zeigt einen Fehler, statt still zu schliessen.
  const [zuLoeschen, setZuLoeschen] = useState<Customer | null>(null);
  const handleDelete = (e: React.MouseEvent, customer: Customer) => {
    e.stopPropagation();
    setZuLoeschen(customer);
  };
  const loeschenBestaetigt = async () => {
    if (!zuLoeschen) return;
    await customersApi.delete(zuLoeschen.id);
    queryClient.invalidateQueries({ queryKey: ['customers'] });
  };

  const { data: users = [] } = useQuery({
    queryKey: ['users'],
    queryFn: () => usersApi.list(),
  });

  const { data, isLoading } = useQuery({
    queryKey: ['customers', { page, per_page: PER_PAGE, ...filters }],
    queryFn: () => customersApi.list({
      page,
      per_page: PER_PAGE,
      search: filters.search || undefined,
      assigned_to: filters.assigned_to || undefined,
      status: filters.status || undefined,
      city: filters.city || undefined,
      sort_by: filters.sort_by,
      sort_order: filters.sort_order,
    }),
  });

  const sortieren = ({ key, direction }: DataTableSort) => {
    updateFilters({ sort_by: key, sort_order: direction });
    setPage(1);
  };

  const hasActiveFilters = filters.search || filters.status || filters.city || filters.assigned_to;

  // Am Handy eine Zeile je Kunde: Firma, darunter Status, Ort und Ansprechpartner.
  const columns: Column<Customer>[] = [
    {
      key: 'company_name',
      header: 'Firma',
      sortable: true,
      mobil: 'titel',
      renderMobil: (customer) => customer.company_name,
      render: (customer) => (
        <div>
          <div className="font-medium">{customer.company_name}</div>
          {customer.display_name && <div className="t-small text-muted-foreground">{customer.display_name}</div>}
        </div>
      ),
    },
    {
      key: 'city',
      header: 'Ort',
      sortable: true,
      mobil: 'unter',
      render: (customer) => customer.city || '–',
    },
    {
      key: 'primary_contact',
      header: 'Ansprechpartner',
      mobil: 'unter',
      renderMobil: (customer) => customer.primary_contact_name || '–',
      render: (customer) =>
        customer.primary_contact_id ? (
          <Link
            to={`/contacts/${customer.primary_contact_id}`}
            className="text-link hover:underline"
            onClick={(e) => e.stopPropagation()}
          >
            {customer.primary_contact_name}
          </Link>
        ) : (
          <span className="text-muted-foreground">–</span>
        ),
    },
    {
      key: 'last_interaction_at',
      header: 'Letzte Interaktion',
      sortable: true,
      mono: true,
      render: (customer) => formatDatum(customer.last_interaction_at),
    },
    {
      key: 'status',
      header: 'Status',
      sortable: true,
      mobil: 'status',
      // Am Handy öffnet die Zeile den Kunden; der Status wechselt dort.
      renderMobil: (customer) => <Status status={customer.status} variant="plain" />,
      render: (customer) => <StatusDropdown customer={customer} />,
    },
    {
      key: 'actions',
      header: '',
      align: 'end',
      render: (customer) => (
        <IconButton
          icon={Trash2}
          label="Kunde löschen"
          variant="ghost"
          size="sm"
          onClick={(e) => handleDelete(e, customer)}
        />
      ),
    },
  ];

  const totalPages = data?.pages ?? 0;

  return (
    <div>
      <ConfirmDialog
        open={zuLoeschen !== null}
        onOpenChange={(offen) => !offen && setZuLoeschen(null)}
        title="Kunde löschen?"
        description={zuLoeschen ? `„${zuLoeschen.company_name}“ wird endgültig gelöscht.` : ''}
        confirmLabel="Kunde löschen"
        tone="danger"
        onConfirm={loeschenBestaetigt}
      />

      <CrmPageHeader
        title="Kunden"
        subtitle={`${data?.total ?? 0} Kunden insgesamt`}
        actions={
          <Button variant="primary" icon={Plus} onClick={() => navigate('/customers/new')}>
            Kunde anlegen
          </Button>
        }
      >
        <div className="w-full pb-3 md:hidden">
          <Input
            icon={Search}
            type="search"
            placeholder="Kunden, Partner, Kontakte"
            aria-label="Kunden, Partner und Kontakte suchen"
            value={mobilSuche}
            onChange={(e) => setMobilSuche(e.target.value)}
          />
          {hasActiveFilters && !suche.aktiv && (
            <Button variant="ghost" size="sm" className="mt-2" onClick={() => { resetFilters(); setPage(1); }}>
              Filter zurücksetzen
            </Button>
          )}
        </div>
        <div className="flex w-full flex-wrap items-center gap-3 pb-4 max-md:hidden">
          <Input
            icon={Search}
            placeholder="Suchen"
            aria-label="Kunden suchen"
            value={filters.search}
            onChange={(e) => { updateFilters({ search: e.target.value }); setPage(1); }}
            className="min-w-[200px] flex-1"
          />
          <Combobox
            className="w-44"
            aria-label="Alle Status"
            value={filters.status}
            onChange={(v) => { updateFilters({ status: v as CustomerStatus | '' }); setPage(1); }}
            options={KUNDE_STATUS_ENTRIES.map(([value, label]) => ({ value, label }))}
            placeholder="Alle Status"
          />
          <Input
            placeholder="Ort"
            aria-label="Nach Ort filtern"
            value={filters.city}
            onChange={(e) => { updateFilters({ city: e.target.value }); setPage(1); }}
            className="w-48"
          />
          <Combobox
            className="w-56"
            aria-label="Alle Mitarbeiter"
            value={filters.assigned_to}
            onChange={(v) => { updateFilters({ assigned_to: v }); setPage(1); }}
            options={users.map((u) => ({ value: u.id, label: u.name }))}
            placeholder="Alle Mitarbeiter"
          />
          {hasActiveFilters && (
            <Button variant="ghost" size="sm" onClick={() => { resetFilters(); setPage(1); }}>
              Filter zurücksetzen
            </Button>
          )}
        </div>
      </CrmPageHeader>

      {suche.aktiv && (
        <div className="md:hidden">
          <Card flush>
            {suche.treffer.length > 0 ? (
              <SuchTreffer
                gross
                treffer={suche.treffer}
                onWahl={(t) => {
                  const ziel = suchZiel(t);
                  if (ziel) navigate(ziel);
                }}
              />
            ) : suche.laedt ? (
              <div className="flex justify-center py-8">
                <Spinner />
              </div>
            ) : (
              <EmptyState title={`Keine Treffer für „${suche.begriff}“`} className="py-8" />
            )}
          </Card>
        </div>
      )}

      <div className={suche.aktiv ? 'max-md:hidden' : undefined}>
      <DataTable<Customer>
        label="Kunden"
        rows={data?.items ?? []}
        columns={columns}
        loading={isLoading}
        onRowClick={(customer) => navigate(`/customers/${customer.id}`)}
        empty={<EmptyState icon={Building2} title="Keine Kunden gefunden" className="py-8" />}
        sort={{ key: filters.sort_by, direction: filters.sort_order }}
        onSortChange={sortieren}
      />

      {data && data.total > 0 && <Pagination page={page} pageCount={totalPages} onPageChange={setPage} />}
      </div>
    </div>
  );
}
